import { TOKENS } from '../components/ui/dashboard-primitives'
import { CATALOG_COPY } from '../copy/catalog'

/** Muscle slug -> label. Mirrors api/muscle_map.py. */
export const MUSCLE_LABELS: Record<string, string> = {
  chest: 'Chest',
  abs: 'Abs',
  obliques: 'Obliques',
  'upper-back': 'Upper back',
  'lower-back': 'Lower back',
  trapezius: 'Traps',
  biceps: 'Biceps',
  triceps: 'Triceps',
  forearm: 'Forearms',
  'front-deltoids': 'Front delts',
  'side-deltoids': 'Side delts',
  'back-deltoids': 'Rear delts',
  quadriceps: 'Quads',
  hamstring: 'Hamstrings',
  // Qualified on purpose: "Abductors" and "Adductors" differ by one letter and
  // sit next to each other on a leg day, which reads as a duplicate.
  adductor: 'Adductors (inner)',
  abductors: 'Abductors (outer)',
  calves: 'Calves',
  gluteal: 'Glutes',
}

/**
 * A known slug's label, else a readable phrase built the way the SERVER builds
 * it -- `api/muscle_map.py`'s `slug.replace("-", " ").capitalize()`: every
 * hyphen replaced, first letter upper, the rest lower.
 *
 * All three clients had a different fallback. This one replaced only the FIRST
 * hyphen (`String.replace` with a string argument is not global) and never
 * capitalised, so `lower-back-erectors` rendered `lower back-erectors` where
 * the server said `Lower back erectors` and iOS said `Lower Back Erectors`.
 * The label TABLE was parity-pinned; the default branch was not, which is
 * exactly where an unfamiliar value lands and where nobody is watching.
 *
 * The server wins because it owns the vocabulary this table is generated from,
 * and is the only one of the three that can be authoritative about a slug the
 * clients have not seen. Spec 1 / D7, woorari/Sport-Tracker#92.
 */
export const muscleLabel = (slug: string) => {
  const known = MUSCLE_LABELS[slug]
  if (known) return known
  const phrase = slug.replaceAll('-', ' ')
  return phrase.charAt(0).toUpperCase() + phrase.slice(1).toLowerCase()
}

/** react-body-highlighter has no lateral deltoid, only front and rear, so the
 *  side delt is drawn on the closest region it can render. Mirrors
 *  `highlight_slugs()` in api/muscle_map.py. Apply ONLY when handing muscles to
 *  <Model>: the library ignores slugs it doesn't know, which would silently
 *  leave the muscle unshaded. Labels keep the anatomically correct slug. */
export const HIGHLIGHT_ALIAS: Record<string, string> = {
  'side-deltoids': 'front-deltoids',
}

export const toHighlightSlugs = (slugs: string[]): string[] =>
  [...new Set(slugs.map((s) => HIGHLIGHT_ALIAS[s] ?? s))]

export const MUSCLE_SCALE = [
  // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
  'rgba(232, 197, 160, 0.18)',
  // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
  'rgba(232, 197, 160, 0.34)',
  TOKENS.amberEmphasis,
  // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
  'rgba(232, 197, 160, 0.72)',
  // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
  'rgba(232, 197, 160, 0.92)',
]

/**
 * The 18 slugs folded into the six groups a filter chip can name.
 *
 * **A COMPLETE PARTITION, and that is the whole point.** `_GROUP_FALLBACK` in
 * `api/muscle_map.py` establishes these six NAMES, and they are the right ones,
 * but it is a fallback used when an exercise name says nothing -- so it is
 * deliberately incomplete: "back" lists only `upper-back`, "core" only `abs`,
 * "legs" omits calves and both adductors, "arms" omits forearm. Reusing it as a
 * filter would leave a machine tagged `calves` matching no chip at all, so it
 * would vanish from every filtered view rather than merely being hard to find.
 * That is the failure this table exists to avoid, and `muscles.test.ts` asserts
 * totality rather than trusting this comment.
 *
 * Client-side only. `api/muscle_map.py` is untouched: `_GROUP_FALLBACK` answers
 * a different question (what does this exercise probably train?) and widening it
 * would change what the server infers.
 *
 * **THE BUILDER'S TRAY READS THIS TABLE TOO, AND THAT DIVERGENCE IS CLOSED**
 * (2026-09-08, on the user's decision). `EquipmentTray.tsx` carried its own
 * four-chip list -- All, Chest, Back, Legs, reaching 12 of the 18 slugs -- and
 * the two surfaces sit one navigation apart in the same flow feeding on
 * identical slugs, so they disagreed about the same machine in a single
 * sitting: a shoulder press tagged `front-deltoids` filed under Shoulders in
 * the picker and Chest in the tray, and a preacher curl tagged only `biceps`
 * findable under Arms in the picker and under no chip at all in the tray. That
 * table is deleted. One vocabulary, defined once, here.
 *
 * The two surfaces still DRAW different chips, and that is not a grouping
 * disagreement: both read `muscleGroupsPresent` over their own items, so each
 * offers exactly the groups its data covers. What is shared is the vocabulary
 * and the derivation; what differs is the input -- one gym's handful of
 * machines against the catalogue's hundreds.
 *
 * `MuscleChips.tsx` is not another one: it prints one device's own slugs
 * through `muscleLabel` and names no group. **Do not add a third.**
 *
 * Key order is display order.
 */
export const MUSCLE_GROUPS = {
  chest: ['chest'],
  back: ['upper-back', 'lower-back', 'trapezius'],
  shoulders: ['front-deltoids', 'side-deltoids', 'back-deltoids'],
  arms: ['biceps', 'triceps', 'forearm'],
  legs: ['quadriceps', 'hamstring', 'gluteal', 'calves', 'adductor', 'abductors'],
  core: ['abs', 'obliques'],
} as const satisfies Record<string, readonly string[]>

export type MuscleGroup = keyof typeof MUSCLE_GROUPS

/** Display order, and the order the chips are drawn in. */
export const MUSCLE_GROUP_ORDER = Object.keys(MUSCLE_GROUPS) as MuscleGroup[]

const GROUP_OF: Record<string, MuscleGroup> = Object.fromEntries(
  MUSCLE_GROUP_ORDER.flatMap((group) =>
    MUSCLE_GROUPS[group].map((slug) => [slug, group] as const),
  ),
)

/** The group a slug belongs to, or null for a slug outside the vocabulary. */
export const muscleGroupOf = (slug: string): MuscleGroup | null =>
  GROUP_OF[slug] ?? null

/**
 * Chip label per group, and the only place these six words are resolved.
 *
 * `Record<MuscleGroup, string>` is total by construction, so a seventh group
 * fails to compile HERE rather than rendering a chip with no name. It lived in
 * `EquipmentGrid.tsx` until the tray adopted the same vocabulary; a second copy
 * beside the second surface would have been the divergence this table just
 * closed, one level down in the labels.
 *
 * The strings come from `CATALOG_COPY` -- the checked pair with
 * `CatalogCopy.swift` -- rather than being written here, so the two clients name
 * a muscle group with the same word or `check_copy_decks.py` says so.
 */
export const MUSCLE_GROUP_LABEL: Record<MuscleGroup, string> = {
  chest: CATALOG_COPY.muscleGroupChest,
  back: CATALOG_COPY.muscleGroupBack,
  shoulders: CATALOG_COPY.muscleGroupShoulders,
  arms: CATALOG_COPY.muscleGroupArms,
  legs: CATALOG_COPY.muscleGroupLegs,
  core: CATALOG_COPY.muscleGroupCore,
}

/**
 * The groups a set of loaded items covers, in display order -- or none at all.
 *
 * A chip vocabulary is READ OFF THE DATA rather than declared, so a chip can
 * never stand for something the list in front of the user does not contain.
 * That matters most in the builder's tray, where the items are one gym's own
 * equipment: seven chips over a six-machine gym is five dead controls.
 *
 * **Pass the UNFILTERED items.** Derived from what survives the current
 * selection, the row collapses to the one group still showing and takes every
 * other chip -- and All, the way back -- with it, under the cursor of the user
 * who just pressed one.
 *
 * FEWER THAN TWO GROUPS DRAWS NOTHING, because one group is not a partition:
 * every chip is then either a no-op or a blackout. `EquipmentGrid.tsx` applies
 * the same rule to its modality chips through its own `canPartition`; the
 * threshold is stated in both places because the two vocabularies are
 * independent, not because either is a copy of the other.
 */
export const muscleGroupsPresent = (slugs: readonly string[]): MuscleGroup[] => {
  const present = new Set(slugs.map(muscleGroupOf).filter(Boolean))
  const found = MUSCLE_GROUP_ORDER.filter((g) => present.has(g))
  return found.length >= 2 ? found : []
}
